/**
 * Centralized tool name registry.
 *
 * All tools MUST import their name from here and tool
 * name MUST be globally unique.
 */
import { Literal, Static, Union } from "runtypes";

export const TOOL_NAMES = {
  // View and search tools
  FIND_TABLE_SCHEMAS: "FindTableSchemas",
  FIND_TABLE_SCHEMAS_SEMANTIC_AUTHORING: "FindTableSchemasSemanticAuthoring",
  RUN_SQL: "RunSQL",
  GET_KERNEL_PACKAGES_INFO: "GetKernelPackagesInfo",
  SEARCH_HEX_DOCS: "SearchHexDocs",
  SEARCH_WEB: "SearchWeb",
  EXTRACT_WEB: "ExtractWeb",
  LOOK_UP_PROJECT: "LookUpProject",
  SEARCH_TOOLS: "SearchTools",
  // Ask/Threads-specific tool for looking at **another** project
  GET_FILE_CONTENTS: "GetFileContents",

  // Org guide tools
  READ_GUIDE: "ReadGuide",

  // Run cells tools
  RUN_APP: "RunApp",

  EXPLORE_PROJECTS: "ExploreProjects",
  FIND_SEMANTIC_COLUMNS: "FindSemanticColumns",
  INSPECT_SEMANTIC_DATA: "InspectSemanticData",
  CELL_SEARCH: "CellSearch",

  // Create cell tools
  CREATE_MARKDOWN_CELL: "CreateMarkdownCell",
  CREATE_SECTION: "CreateSection",
  CREATE_SQL_CELL: "CreateSqlCell",
  CREATE_PYTHON_CELL: "CreatePythonCell",
  CREATE_PIVOT_CELL: "CreatePivotCell",
  CREATE_CHART_CELL: "CreateChartCell",
  CREATE_METRIC_CELL: "CreateMetricCell",
  CREATE_INPUT_CELL: "CreateInputCell",
  CREATE_SANKEY_CELL: "CreateSankeyCell",

  // Edit cell tools
  EDIT_REPLACE_MARKDOWN_CELL: "EditReplaceMarkdownCell",
  EDIT_REPLACE_SQL_CELL: "EditReplaceSqlCell",
  EDIT_REPLACE_PYTHON_CELL: "EditReplacePythonCell",
  EDIT_REPLACE_CHART_CELL: "EditReplaceChartCell",

  // Diff edit tools
  EDIT_MARKDOWN_CELL_DIFF: "EditMarkdownCellDiff",
  EDIT_SQL_CELL_DIFF: "EditSqlCellDiff",
  EDIT_PYTHON_CELL_DIFF: "EditPythonCellDiff",
  EDIT_PIVOT_CELL: "EditPivotCell",
  EDIT_METRIC_CELL: "EditMetricCell",
  EDIT_INPUT_CELL: "EditInputCell",

  // Vis tools
  STYLE_CHART_CELL: "StyleChartCell",
  ADD_EDIT_DELETE_CHART_CELL_SERIES: "AddEditDeleteChartCellSeries",
  ADD_EDIT_DELETE_REFERENCE_LINE_ON_CHART_CELL:
    "AddEditDeleteReferenceLineOnChartCell",

  // Vis cell conversion tools
  UPGRADE_LEGACY_CHART_CELL: "UpgradeLegacyChartCell",
  DUPLICATE_LEGACY_PIVOT_CELL: "DuplicateLegacyPivotCell",

  // Other cell tools
  MOVE_CELLS: "MoveCells",
  DELETE_CELLS: "DeleteCells",
  SET_CELLS_IN_APP: "SetCellsInApp",

  // App layout tools
  VIEW_APP_LAYOUT: "ViewAppLayout",
  MOVE_APP_ROW: "MoveAppRow",
  EDIT_APP_ROW: "EditAppRow",
  DELETE_APP_ROW: "DeleteAppRow",
  CREATE_APP_ROW: "CreateAppRow",
  CREATE_APP_TAB: "CreateAppTab",
  EDIT_APP_TAB: "EditAppTab",
  DELETE_APP_TAB: "DeleteAppTab",
  PUBLISH_APP: "PublishApp",
  REMOVE_ALL_APP_ELEMENTS: "RemoveAllAppElements",
  RESET_APP_LAYOUT: "ResetAppLayout",
  UPDATE_CELL_DISPLAY_SETTINGS: "UpdateCellDisplaySettings",
  SET_CELL_HEIGHT: "SetCellHeight",

  // Other tools
  CREATE_SEMANTIC_TABLE: "CreateSemanticTable",

  // Semantic authoring tools
  BOOTSTRAP_SEMANTIC_DATA_MODEL_FROM_TABLE:
    "BootstrapSemanticDataModelFromTable",
  CREATE_SEMANTIC_PROJECT_FILE: "CreateSemanticProjectFile",
  EDIT_SEMANTIC_PROJECT_FILE: "EditSemanticProjectFile",
  RENAME_SEMANTIC_PROJECT_FILE: "RenameSemanticProjectFile",
  VALIDATE_SEMANTIC_PROJECT: "ValidateSemanticProject",

  // Review Agent tools
  REVIEW_READ_GUIDE: "ReadOrgGuide",
  REVIEW_READ_RULES: "ReadWorkspaceRules",
  REVIEW_READ_METADATA: "ReadMetadata",
  REVIEW_READ_THREAD_MESSAGES: "ReadThreadMessages",
  REVIEW_PROPOSE_CREATE_ORG_GUIDE: "ProposeCreateOrgGuide",
  REVIEW_PROPOSE_UPDATE_ORG_GUIDE: "ProposeUpdateOrgGuide",
  REVIEW_PROPOSE_UPDATE_METADATA: "ProposeUpdateMetadata",
  REVIEW_PROPOSE_ENDORSE_RESOURCE: "ProposeEndorseResource",

  // Data discovery tools
  DELEGATE_TO_DATA_DISCOVERY_AGENT: "DelegateToDataDiscoveryAgent", // Tool call to delegate search to a data discovery subagent.
  LIST_DATA_CONNECTIONS: "ListDataConnections",

  // Visualization subagent tools
  DELEGATE_TO_VISUALIZATION_AGENT: "DelegateToVisualizationAgent", // Tool call to delegate visualization work to a visualization subagent.

  // Memory tools
  READ_PAST_THREAD_SUMMARY: "ReadPastThreadSummary",

  // GitHub repo tools
  GITHUB_REPO_DOWNLOAD: "GithubRepoDownload",
  REPO_SHELL_EXEC: "RepoShellExec",

  // Chat with app tools
  SEARCH_VALID_INPUT_VALUES: "SearchValidInputValues",
  SET_APP_CELL_FILTERS_AND_SORT: "SetAppCellFiltersAndSort",
  SET_APP_INPUTS: "SetAppInputs",
  VIEW_DEFINITION: "ViewDefinition",

  // Data app tools
  DELEGATE_TO_DATA_APP_AGENT: "DelegateToDataAppAgent",
  READ_DATA_APP_FILE: "ReadDataAppFile",
  WRITE_DATA_APP_FILE: "WriteDataAppFile",
  EDIT_DATA_APP_FILE: "EditDataAppFile",
  LIST_DATA_APP_FILES: "ListDataAppFiles",
  DELETE_DATA_APP_FILE: "DeleteDataAppFile",
  READ_DATA_APP_CONSOLE: "ReadDataAppConsole",
  READ_DATA_APP_SCREENSHOT: "ReadDataAppScreenshot",

  // Legacy tool names kept for historical data parsing
  OUTLINE_PROJECT: "OutlineProject",

  // Review suggestion tools
  SUGGEST_REVIEW: "SuggestReview",

  // Eval-only tools
  SUBMIT_ANSWER: "SubmitAnswer",
} as const;

/**
 * Tool names that delegate to subagents. Used to identify delegation
 * tool calls/results for UI filtering and block segmentation.
 */
export const SUBAGENT_DELEGATION_TOOL_NAMES: ReadonlySet<string> = new Set([
  TOOL_NAMES.DELEGATE_TO_DATA_APP_AGENT,
  TOOL_NAMES.DELEGATE_TO_DATA_DISCOVERY_AGENT,
  TOOL_NAMES.DELEGATE_TO_VISUALIZATION_AGENT,
  TOOL_NAMES.DELEGATE_TO_DATA_APP_AGENT,
]);

/**
 * Tool calls/results for these tools are hidden from the UI entirely.
 * A thinking block containing only messages from these tools should not
 * be shown to the user (it would appear as an empty collapsible block).
 */
export const UI_HIDDEN_TOOL_NAMES: ReadonlySet<string> = new Set([
  ...SUBAGENT_DELEGATION_TOOL_NAMES,
  TOOL_NAMES.SEARCH_TOOLS,
]);

type ToolNameValue = (typeof TOOL_NAMES)[keyof typeof TOOL_NAMES];

const TOOL_NAME_LITERALS = Object.values(TOOL_NAMES).map((name) =>
  Literal(name),
) as [Literal<ToolNameValue>, ...Literal<ToolNameValue>[]];

export const ToolNameLiteral = Union(...TOOL_NAME_LITERALS);
export type ToolName = Static<typeof ToolNameLiteral>;
